/**
 * @file useMinimumHold.ts
 * @brief Keeps a boolean "on" until a minimum duration has elapsed after
 *        it first became active — used so branded boot UI is visible even
 *        when auth resolves instantly.
 *
 * @author DES
 * @date 17-07-2026
 */

import { useEffect, useRef, useState } from "react";
import { FLIP_BOARD_DONE_MS, flipBoardRemainingMs } from "@/components/brand/BrandFlipBoard";

/**
 * Hold long enough for the loader's animation to finish: one full
 * stroke→fill draw cycle (2.2s), or the Flip board's ending, whichever is
 * longer. Was a flat 2200, which cut the Flip board's logo zoom (2.10–2.45s)
 * off after 0.1s (03-10-2026).
 */
export const BOOT_LOADER_MIN_MS = Math.max(2200, FLIP_BOARD_DONE_MS);

/**
 * While `active` is true, always returns true. After `active` flips false,
 * stays true until `minMs` has passed since the first activation. If
 * `active` was never true, returns false immediately (no artificial delay).
 */
export function useMinimumHold(active: boolean, minMs: number): boolean {
  const activatedAtRef = useRef<number | null>(null);
  const [holding, setHolding] = useState(active);

  useEffect(() => {
    if (active) {
      if (activatedAtRef.current === null) {
        activatedAtRef.current = Date.now();
      }
      setHolding(true);
      return;
    }

    if (activatedAtRef.current === null) {
      setHolding(false);
      return;
    }

    // Also wait for a Flip board that is still playing its ending: its clock
    // can start well after `active` did (03-10-2026).
    const remaining = Math.max(
      minMs - (Date.now() - activatedAtRef.current),
      flipBoardRemainingMs(),
    );
    if (remaining <= 0) {
      activatedAtRef.current = null;
      setHolding(false);
      return;
    }

    const id = window.setTimeout(() => {
      activatedAtRef.current = null;
      setHolding(false);
    }, remaining);

    return () => window.clearTimeout(id);
  }, [active, minMs]);

  return holding;
}
